import { addDays, format, parseISO } from "date-fns";

import {
  type NextDayCacheEntry,
  getDatesKey,
} from "./deriveAvailabilityCacheRegistry";
import {
  type DiscountAvailabilityStrategy,
  firstTableDiscountAvailabilityStrategy,
} from "./discountAvailabilityStrategy";
import expandSessionsForFirebase from "./expandSessionsForFirebase";
import { DATE_FORMAT, getLast } from "../../dependencies/utils";
import { type FirebaseAllAvailabilityById } from "../../model/firebaseAllAvailability";

const computeIsNextDiscountDayAvailable = (
  sourceRef: FirebaseAllAvailabilityById[number] | undefined,
  discountDatesToShow: readonly string[],
  sessions: SessionType[],
  people: number,
  strategy: DiscountAvailabilityStrategy,
): boolean => {
  if (!sourceRef) {
    return false;
  }

  const lastDateToShow = getLast([...discountDatesToShow]);
  const nextDate = format(
    addDays(parseISO(lastDateToShow ?? ""), 1),
    DATE_FORMAT,
  );

  // Next-day lookahead ignores the Last Table narrowing.
  return expandSessionsForFirebase(sessions, false).some((sessionType) =>
    sourceRef[sessionType]?.[nextDate]?.some(
      (slot) =>
        strategy.includeSlot(slot) && strategy.isSlotAvailable(slot, people),
    ),
  );
};

export const getCachedIsNextDiscountDayAvailable = (
  cache: Map<number, NextDayCacheEntry>,
  restaurantId: number,
  sourceRef: FirebaseAllAvailabilityById[number] | undefined,
  discountDatesToShow: readonly string[],
  sessions: SessionType[],
  people: number,
  strategy: DiscountAvailabilityStrategy = firstTableDiscountAvailabilityStrategy,
): boolean => {
  const datesKey = getDatesKey(discountDatesToShow);
  // Include strategy mode so FT and walk-in next-day caches do not collide.
  const sessionsKey = `${strategy.modeKey}\0${sessions.join("\0")}`;
  const cached = cache.get(restaurantId);

  if (
    cached &&
    cached.sourceRef === sourceRef &&
    cached.datesKey === datesKey &&
    cached.sessionsKey === sessionsKey &&
    cached.people === people
  ) {
    return cached.value;
  }

  const value = computeIsNextDiscountDayAvailable(
    sourceRef,
    discountDatesToShow,
    sessions,
    people,
    strategy,
  );

  cache.set(restaurantId, {
    sourceRef,
    datesKey,
    sessionsKey,
    people,
    value,
  });

  return value;
};
